<template>
  <div>
    <div class="row mb-3 align-items-center">
      <div class="col-sm-12 col-md-6">
        <label class="d-inline-flex align-items-center mb-0">
          Show
          <b-form-select class="form-select form-select-sm mx-2" v-model="perPage" size="sm"
            :options="[10, 25, 50, 100]"></b-form-select>
          entries
        </label>
      </div>
      <div class="col-sm-12 col-md-6 text-md-end mt-3 mt-md-0">
        <b-form-input v-model="filter" type="search" placeholder="Search approved loans..."
          class="form-control form-control-sm d-inline-block w-auto"></b-form-input>
      </div>
    </div>
    <div class="table-responsive">
      <b-table :items="Items" :fields="Fields" responsive="sm" :per-page="perPage" :current-page="currentPage"
        :filter="filter" :busy="IsBusy" show-empty class="align-middle table-nowrap table-hover">

        <template #table-busy>
          <div class="text-center text-primary my-5">
            <b-spinner class="align-middle" type="grow"></b-spinner>
            <div class="mt-2 fw-medium">Loading Loan Disbursements...</div>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-5">
            <div class="mb-3">
              <i class="mdi mdi-cash-remove text-muted" style="font-size: 64px;"></i>
            </div>
            <h5 class="text-dark">No Pending Disbursements</h5>
            <p class="text-muted mb-4">There are currently no approved loans awaiting disbursement.</p>
          </div>
        </template>

        <template #cell(index)="data">{{ data.index + 1 }}</template>

        <template #cell(loan_number)="data">
          <span class="fw-medium">{{ data.item.loan_number }}</span>
          <small class="d-block text-primary fw-bold">{{ data.item.member_name }} - {{ data.item.member_id }}</small>
        </template>

        <template #cell(approved_date)="data">
          <span>{{ formatDate(data.item.approved_date) }}</span>
          <small class="d-block fw-bold">Period: <span class="text-primary">{{ data.item.period }} Months</span></small>
        </template>

        <template #cell(loan_product)="data">
          <span class="fw-medium">{{ data.item.loan_product }}</span>
          <small class="d-block fw-bold">Rate: <span class="text-primary">{{ data.item.rate }}% {{ data.item.rate_type
            == 'MONTHLY' ? 'Monthly' : 'Annually' }}</span></small>
        </template>

        <template #cell(amount)="data">
          <div class="mb-1">
            <span class="text-muted small">Principal:</span> <span class="fw-bold">{{ userData.currency }} {{
              commas(data.item.amount) }}</span>
          </div>
          <div class="mb-1" v-if="data.item.charges > 0">
            <span class="text-muted small">Charges:</span> <span class="text-danger">- {{ userData.currency }} {{
              commas(data.item.charges) }}</span>
          </div>
          <div class="border-top pt-1 mt-1" v-if="data.item.charges > 0">
            <span class="text-muted small fw-bold">Net Disbursed:</span> <span class="text-success fw-bold">{{
              userData.currency }} {{ commas(data.item.net_disbursed_amount) }}</span>
          </div>
          <!-- Dynamic Payment Structure & Installment -->
          <small class="d-block text-muted mt-1" v-if="data.item.payment_structure">
            <i class="mdi mdi-calendar-sync"></i> {{ data.item.payment_structure.charAt(0) +
              data.item.payment_structure.slice(1).toLowerCase() }} Installments
            <div v-if="data.item.installment_amount" class="text-dark mt-1">
              {{ userData.currency }}
              <span v-if="data.item.installment_amount.min !== undefined">
                {{ commas(data.item.installment_amount.min.toFixed(0)) }} - {{
                  commas(data.item.installment_amount.max.toFixed(0)) }}
              </span>
              <span v-else>
                {{ commas(data.item.installment_amount.toFixed(0)) }}
              </span>
            </div>
          </small>
        </template>

        <template #cell(interest_display)="data">
          <span class="text-danger fw-medium" v-if="data.item.best_interest !== undefined">
            {{ userData.currency }} {{ commas(data.item.best_interest.toFixed(0)) }} - {{
              commas(data.item.worst_interest.toFixed(0)) }}
          </span>
          <span class="text-danger fw-medium" v-else>
            {{ userData.currency }} {{ commas(data.item.interest.toFixed(0)) }}
          </span>
        </template>

        <template #cell(actions)="data">
          <div class="gap gap-1 d-flex justify-content-start">
            <b-button title="Revert to Application" size="sm" variant="danger" @click="promptRevert(data.item)">
              <i class="mdi mdi-undo"></i>
            </b-button>
            <b-button title="Disburse Loan" size="sm" variant="primary" @click="promptDisburse(data.item)">
              <i class="mdi mdi-cash"></i> Disburse
            </b-button>
          </div>
        </template>
      </b-table>
    </div>

    <div class="d-flex justify-content-end mt-3" v-if="List > 0">
      <b-pagination v-model="currentPage" :total-rows="List" :per-page="perPage"></b-pagination>
    </div>

    <b-modal id="revert-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-danger mb-2"><i class="mdi mdi-undo-variant"></i></div>
        <h4 class="text-danger mb-3">Revert Application?</h4>
        <p class="fs-6 text-muted mb-4">
          Are you sure you want to revert loan <strong>{{ selectedApplication ? selectedApplication.loan_number : ''
          }}</strong> back to pending status?
        </p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('revert-modal')" class="me-3 px-4"
          :disabled="isActionLoading">Cancel</b-button>
        <b-button variant="danger" @click="executeRevert" class="px-4" :disabled="isActionLoading">
          <span v-if="isActionLoading"><b-spinner small class="me-1"></b-spinner> Reverting...</span>
          <span v-else><i class="mdi mdi-undo me-1"></i> Yes, Revert</span>
        </b-button>
      </div>
    </b-modal>

    <b-modal id="disburse-modal" size="xl" title="Disburse Loan Application" hide-footer hide-header
      @hidden="resetWizard">

      <div class="modal-header border-0 pb-0 mb-4">
        <div class="d-flex align-items-center">
          <div class="bg-primary bg-opacity-10 p-2 me-3"
            style="border-radius: 20rem; width: 3rem; height: 3rem; display: flex; align-items: center; justify-content: center;">
            <i class="mdi mdi-cash text-primary" style="font-size: 2.0rem;"></i>
          </div>
          <div>
            <h5 class="modal-title fw-bold mb-0">Disburse Loan <span v-if="selectedApplication"
                class="text-muted fw-normal">#{{ selectedApplication.loan_number }}</span></h5>
            <small class="text-muted">Issue funds and officially start the loan lifecycle</small>
          </div>
        </div>
      </div>

      <form @submit.prevent>
        <div v-if="wizardStep === 1" class="px-2">
          <div class="alert alert-info border-info bg-soft-info mb-4" v-if="selectedApplication">
            <i class="mdi mdi-information-outline me-1"></i>
            You are disbursing <strong>{{ userData.currency }} {{ commas(selectedApplication.net_disbursed_amount)
            }}</strong> to <strong>{{ selectedApplication.member_name }}</strong>.
          </div>

          <div class="row">
            <div class="col-md-6 form-group mb-4">
              <label class="form-label">Disbursement Date <span class="text-danger">*</span></label>
              <input type="date" v-model="disburseForm.disbursement_date" :max="todayDate" class="form-control"
                required>
              <div class="form-text text-muted mt-1"><i class="mdi mdi-calendar"></i> Cannot be a future date.</div>
            </div>

            <div class="col-md-6 form-group mb-4">
              <label class="form-label">Mode of Payment <span class="text-danger">*</span></label>
              <select v-model="disburseForm.mode_of_payment" class="form-select" required>
                <option :value="null" disabled>-- Select Payment Mode --</option>
                <option value="CASH">Cash</option>
                <option value="BANK_TRANSFER">Bank Transfer</option>
                <option value="MOBILE_MONEY">Mobile Money</option>
                <option value="TANDIPAY">TandiPay</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div class="col-md-12 form-group mb-4" v-if="disburseForm.mode_of_payment === 'OTHER'">
              <label class="form-label">Payment Reference <span class="text-danger">*</span></label>
              <input type="text" v-model="disburseForm.reference" class="form-control"
                placeholder="Specify the payment method/reference..." required>
            </div>

            <div class="col-md-12 form-group mb-4" v-else>
              <label class="form-label">Payment Reference <span class="small text-secondary">(optional)</span></label>
              <input type="text" v-model="disburseForm.reference" class="form-control"
                placeholder="e.g. Transaction ID or Cheque No">
            </div>
          </div>
        </div>

        <div v-if="wizardStep === 2" class="px-2 px-md-4">
          <div v-if="isPreviewLoading || isLoadingDetails" class="text-center py-5 text-primary">
            <b-spinner></b-spinner>
            <p class="mt-2 text-muted">Calculating final repayment schedule and compiling details...</p>
          </div>

          <div v-else-if="detailsError" class="text-center py-5">
            <i class="mdi mdi-alert-circle-outline display-4 text-danger mb-2"></i>
            <h6 class="text-danger">Failed to load application details.</h6>
            <b-button size="sm" variant="outline-danger" class="mt-2" @click="fetchPreviewAndProceed">Retry</b-button>
          </div>

          <div v-else-if="detailedApplication && previewExpiryDate">
            <b-alert variant="warning" show class="mb-4 fw-medium text-center border-warning">
              <i class="mdi mdi-alert-outline fs-5"></i> Warning: Once a loan is disbursed, it cannot be reverted.
              This permanently alters live financial records.
            </b-alert>

            <div class="row">
              <div class="col-lg-6 pe-lg-4 mb-4 mb-lg-0 border-end-lg">
                <h6 class="text-uppercase text-muted fw-bold small mb-2"><i class="mdi mdi-account-circle me-1"></i>
                  Applicant Details</h6>
                <div class="d-flex align-items-center mb-4 p-2 bg-light rounded border shadow-sm">
                  <div class="avatar-md me-3 flex-shrink-0">
                    <span class="avatar-title rounded-circle bg-soft-primary text-white fs-2">
                      {{ selectedApplication.member_name.charAt(0) }}
                    </span>
                  </div>
                  <div>
                    <h5 class="mb-1 text-dark fw-bold">{{ selectedApplication.member_name }}</h5>
                    <p class="mb-0 text-muted small">
                      Member ID: <strong>{{ selectedApplication.member_id }}</strong>
                    </p>
                  </div>
                </div>

                <div class="card border-primary border-opacity-50 shadow-sm mb-0">
                  <div class="card-body bg-soft-primary bg-opacity-10 rounded p-4">
                    <p class="text-uppercase fw-bold text-primary small mb-1"><i class="mdi mdi-cash-multiple me-1"></i>
                      Net Issued Funds</p>
                    <h1 class="text-success fw-bold mb-2 display-6">{{ userData.currency }} {{
                      commas(selectedApplication.net_disbursed_amount) }}</h1>

                    <p class="text-muted small mb-4">
                      Gross Principal: <strong>{{ userData.currency }} {{ commas(detailedApplication.loan.amount)
                      }}</strong>
                      <span v-if="selectedApplication.charges > 0">
                        <br>Deducted Charges: <strong class="text-danger">- {{ userData.currency }} {{
                          commas(selectedApplication.charges) }}</strong>
                      </span>
                    </p>

                    <h5 class="text-dark fw-bold mb-3">{{ detailedApplication.product.name }}</h5>

                    <div
                      class="d-flex justify-content-between border-bottom border-primary border-opacity-25 pb-2 mb-3">
                      <span class="text-muted">Interest Profile:</span>
                      <span class="fw-bold text-dark">{{ detailedApplication.loan.rate }}% {{
                        detailedApplication.loan.rate_type }} ({{ detailedApplication.loan.interest_type.replace('_',
                          '')
                        }})</span>
                    </div>

                    <div
                      class="d-flex justify-content-between border-bottom border-primary border-opacity-25 pb-2 mb-3">
                      <span class="text-muted">Loan Duration:</span>
                      <span class="fw-bold text-dark">{{ detailedApplication.loan.period }} Months</span>
                    </div>

                    <div class="d-flex justify-content-between border-bottom border-primary border-opacity-25 pb-2 mb-3"
                      v-if="detailedApplication.loan.payment_structure">
                      <span class="text-muted">Payment Frequency:</span>
                      <span class="fw-bold text-dark">{{ detailedApplication.loan.payment_structure }}</span>
                    </div>

                    <div class="d-flex justify-content-between border-bottom border-primary border-opacity-25 pb-2 mb-3"
                      v-if="getFineStructure(detailedApplication.loan.loan_fine_id)">
                      <span class="text-muted">Late Fine Policy:</span>
                      <span class="fw-bold text-dark">{{ getFineStructure(detailedApplication.loan.loan_fine_id)
                      }}</span>
                    </div>

                    <div class="d-flex justify-content-between align-items-center pt-1 mb-2">
                      <span class="text-muted fw-bold">Expected Total Interest:</span>
                      <span class="text-danger fw-bold fs-5" v-if="detailedApplication.interest.min !== undefined">
                        {{ userData.currency }} {{ commas(detailedApplication.interest.min.toFixed(0)) }} <span
                          class="text-muted fs-6 fw-normal mx-1">to</span> {{
                            commas(detailedApplication.interest.max.toFixed(0)) }}
                      </span>
                      <span class="text-danger fw-bold fs-5" v-else>
                        {{ userData.currency }} {{ commas(detailedApplication.interest.toFixed(0)) }}
                      </span>
                    </div>

                    <div class="alert alert-secondary border-secondary mt-3 mb-0 py-2 px-3"
                      v-if="detailedApplication.loan.payment_structure && detailedApplication.installment_amount">
                      <div class="d-flex justify-content-between align-items-center">
                        <span class="small fw-medium"><i class="mdi mdi-calendar-refresh me-1"></i> {{
                          detailedApplication.loan.payment_structure }} Est. Installment:</span>
                        <span class="fw-bold" v-if="detailedApplication.installment_amount.min !== undefined">
                          {{ userData.currency }} {{ commas(detailedApplication.installment_amount.min.toFixed(0)) }} -
                          {{
                            commas(detailedApplication.installment_amount.max.toFixed(0)) }}
                        </span>
                        <span class="fw-bold" v-else>
                          {{ userData.currency }} {{ commas(detailedApplication.installment_amount.toFixed(0)) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-6 ps-lg-4">
                <h6 class="text-uppercase text-muted fw-bold small mb-2"><i class="mdi mdi-bank-transfer me-1"></i>
                  Disbursement Details</h6>

                <ul class="list-group list-group-flush mb-4 border rounded shadow-sm">
                  <li class="list-group-item d-flex justify-content-between align-items-center py-3">
                    <span class="text-muted">Mode of Payment</span>
                    <span class="fw-bold text-dark">{{ disburseForm.mode_of_payment.replace('_', ' ') }}</span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between align-items-center py-3"
                    v-if="disburseForm.reference">
                    <span class="text-muted">Payment Reference</span>
                    <span class="fw-bold text-dark">{{ disburseForm.reference }}</span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between align-items-center py-3">
                    <span class="text-muted">Disbursement Date</span>
                    <span class="fw-bold text-dark">{{ formatDate(disburseForm.disbursement_date) }}</span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between align-items-center py-3 bg-soft-warning">
                    <span class="text-dark fw-medium">Calculated Expiry Date</span>
                    <span class="fw-bold text-danger"><i class="mdi mdi-calendar-alert me-1"></i> {{
                      formatDate(previewExpiryDate) }}</span>
                  </li>
                  <li class="list-group-item d-flex justify-content-between align-items-center py-3"
                    v-if="detailedApplication.loan.grace_period > 0">
                    <span class="text-muted">Grace Period</span>
                    <span class="fw-bold text-dark">{{ detailedApplication.loan.grace_period }} Days</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

        </div>

        <div class="d-flex justify-content-between mt-4 pt-3 border-top">
          <b-button v-if="wizardStep > 1" variant="light" @click="wizardStep--"><i class="mdi mdi-arrow-left"></i>
            Back</b-button>
          <div v-else></div>

          <div class="d-flex gap-2">
            <b-button variant="light" @click="$bvModal.hide('disburse-modal')" class="me-2"
              :disabled="isActionLoading">Cancel</b-button>

            <b-button v-if="wizardStep === 1" variant="primary" @click="fetchPreviewAndProceed" class="px-4">
              Next <i class="mdi mdi-arrow-right"></i>
            </b-button>

            <b-button v-if="wizardStep === 2" variant="primary" @click="executeDisburse" class="px-4"
              :disabled="isActionLoading || isPreviewLoading || detailsError">
              <span v-if="isActionLoading"><b-spinner small class="me-1"></b-spinner> Processing...</span>
              <span v-else><i class="mdi mdi-cash-fast me-1"></i> Confirm & Disburse</span>
            </b-button>
          </div>
        </div>
      </form>
    </b-modal>

  </div>
</template>

<script>
import axios from "axios";
import {
  loandisbursementComputed, loandisbursementMethods,
  loanfinesComputed, loanfinesMethods
} from "@/state/helpers";

export default {
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      perPage: 10,
      currentPage: 1,
      filter: null,

      selectedApplication: null,
      isActionLoading: false,

      wizardStep: 1,
      isPreviewLoading: false,
      previewExpiryDate: null,

      isLoadingDetails: false,
      detailsError: false,
      detailedApplication: null,

      disburseForm: {
        disbursement_date: new Date().toISOString().split('T')[0],
        mode_of_payment: null,
        reference: ''
      },

      fields: [
        { key: 'index', label: '#', class: 'align-middle', visible: true },
        { key: 'loan_number', label: 'Loan Ref', sortable: true, class: 'align-middle', visible: true },
        { key: 'loan_product', label: 'Product Details', sortable: false, class: 'align-middle', visible: true },
        { key: 'approved_date', label: 'Approved On', sortable: true, class: 'align-middle', visible: true },
        { key: 'amount', label: 'Disbursement Info', sortable: true, class: 'align-middle', visible: true },
        { key: 'interest_display', label: 'Expected Interest', class: 'align-middle', visible: true },
        { key: 'actions', label: 'Actions', class: 'align-middle', visible: true },
        { key: 'id', label: 'ID', class: 'align-middle', visible: false },
        { key: 'loan_product_id', label: 'Loan Product ID', class: 'align-middle', visible: false },
        { key: 'member_id', label: 'Member ID', class: 'align-middle', visible: false }
      ],
    };
  },
  computed: {
    ...loandisbursementComputed,
    ...loanfinesComputed,
    List() { return this.loandisbursements ? this.loandisbursements.length : 0; },
    Fields() { return this.fields.filter(field => field.visible); },
    Items() { return this.loandisbursements ? this.loandisbursements.map((d) => ({ ...d })) : []; },
    IsBusy() { return this.isLoading; },
    todayDate() { return new Date().toISOString().split('T')[0]; }
  },
  async mounted() {
    await this.fetchLoanFines();
  },
  methods: {
    ...loandisbursementMethods,
    ...loanfinesMethods,

    commas(x) { return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0; },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date)) return dateString;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    },

    getFineStructure(fineId) {
      if (!fineId || !this.loanfines) return null;
      const fine = this.loanfines.find(f => f.id === fineId);
      return fine ? fine.type.replace(/_/g, ' ') : null;
    },

    promptRevert(item) {
      this.selectedApplication = item;
      this.$bvModal.show('revert-modal');
    },

    promptDisburse(item) {
      this.selectedApplication = item;
      this.resetWizard();
      this.$bvModal.show('disburse-modal');
      this.fetchLoanDetails(item.id);
    },

    async fetchLoanDetails(id) {
      this.isLoadingDetails = true;
      this.detailsError = false;
      try {
        const res = await axios.get(`/loans/${id}/details`);
        this.detailedApplication = res.data;
      } catch (error) {
        this.detailsError = true;
      } finally {
        this.isLoadingDetails = false;
      }
    },

    resetWizard() {
      this.wizardStep = 1;
      this.previewExpiryDate = null;
      this.detailedApplication = null;
      this.disburseForm = {
        disbursement_date: this.todayDate,
        mode_of_payment: null,
        reference: ''
      };
    },

    async executeRevert() {
      if (!this.selectedApplication) return;
      this.isActionLoading = true;

      try {
        const response = await axios.post(`/loans/revert/${this.selectedApplication.id}`);
        if (response.status == 200) {
          this.$toast.success('Loan application reverted to pending successfully.');
          await this.fetchLoanDisbursements();
          this.$bvModal.hide('revert-modal');
        } else {
          this.$toast.error('Failed to revert application.');
        }
      } catch (error) {
        this.$toast.error('Failed to revert application.');
      } finally {
        this.isActionLoading = false;
        this.selectedApplication = null;
      }
    },

    async fetchPreviewAndProceed() {
      if (!this.disburseForm.disbursement_date) {
        this.$toast.error("Disbursement date is required.");
        return;
      }
      if (this.disburseForm.disbursement_date > this.todayDate) {
        this.$toast.error("Disbursement date cannot be in the future.");
        return;
      }
      if (!this.disburseForm.mode_of_payment) {
        this.$toast.error("Please select a mode of payment.");
        return;
      }
      if (this.disburseForm.mode_of_payment === 'OTHER' && !this.disburseForm.reference.trim()) {
        this.$toast.error("A payment reference is required when 'Other' is selected.");
        return;
      }

      this.wizardStep = 2;
      this.isPreviewLoading = true;

      try {
        const res = await axios.post(`/loans/${this.selectedApplication.id}/preview-disbursement`, {
          disbursement_date: this.disburseForm.disbursement_date
        });
        this.previewExpiryDate = res.data.expiry_date;

        if (!this.detailedApplication && !this.isLoadingDetails) {
          await this.fetchLoanDetails(this.selectedApplication.id);
        }
      } catch (error) {
        this.$toast.error("Failed to calculate repayment schedule.");
        this.wizardStep = 1;
      } finally {
        this.isPreviewLoading = false;
      }
    },

    async executeDisburse() {
      if (!this.selectedApplication) return;
      this.isActionLoading = true;

      try {
        await axios.post(`/loans/disburse/${this.selectedApplication.id}`, this.disburseForm);
        this.$toast.success('Loan successfully disbursed and active!');
        this.$bvModal.hide('disburse-modal');
        await this.fetchLoanDisbursements();
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Failed to disburse loan.');
      } finally {
        this.isActionLoading = false;
        this.selectedApplication = null;
      }
    }
  }
};
</script>

<style scoped>
@media (min-width: 992px) {
  .border-end-lg {
    border-right: 1px solid #dee2e6;
  }
}
</style>